import React from "react"
import { useNavigate } from "react-router-dom"
import { getMyStudentsForReport } from "../../modules/bmd/services/bmd-report.service"
import type { BmdReportStudent } from "../../modules/bmd/types/bmd-report.types"
import { initials } from "./studentReportModel"
import "./student-report.css"

const StudentReportListPage: React.FC = () => {
  const navigate = useNavigate()
  const [students, setStudents] = React.useState<BmdReportStudent[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState<string>("")

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    getMyStudentsForReport(controller.signal)
      .then(data => {
        setStudents(data.students)
        setLoading(false)
      })
      .catch(err => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load students")
        setLoading(false)
      })
    return () => controller.abort()
  }, [])

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return students
    return students.filter(
      s =>
        s.name.toLowerCase().includes(q) ||
        (s.enrolment_year_level_name || "").toLowerCase().includes(q)
    )
  }, [students, search])

  return (
    <div className="sr-page">
      <header className="sr-header">
        <div className="sr-header-inner">
          <div className="sr-row">
            <h1 className="sr-title">Student Report</h1>
          </div>
        </div>
      </header>

      <main className="sr-main">
        <section className="sr-card sr-list">
          <div className="sr-row">
            <input
              type="search"
              className="sr-search"
              placeholder="Search by student name or year"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span className="sr-muted">
              {filtered.length} student{filtered.length === 1 ? "" : "s"}
            </span>
          </div>

          {loading ? (
            <div className="sr-state">Loading…</div>
          ) : error ? (
            <div className="sr-state sr-state-error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="sr-state">No students found.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="sr-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Year</th>
                    <th style={{ width: "180px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.pk_student_key}>
                      <td>
                        <div className="sr-table-student">
                          <span className="sr-avatar">{initials(s.name)}</span>
                          {s.name}
                        </div>
                      </td>
                      <td className="sr-muted">
                        {s.enrolment_year_level_name || "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="sr-btn-primary"
                          onClick={() =>
                            navigate(`/student-report/${s.pk_student_key}`)
                          }
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

export default StudentReportListPage
